import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { simpleCopy } from '@/utils/deepCopy';
import { TableCommonViewModel } from '@/viewModels/tableContent';
import { TableContentHeaders, addTableContentHeader, additionalHeaders } from '../domains/valueObjects/TableContentHeaders';
import { businessContentName } from '@/appLogic/business/businessTableHeaders';
import { Operator, getOperators } from '../domains/valueObjects/Operator';
import { InputFormula, addInputFormula, removeInputFormula } from '../domains/valueObjects/InputFormula';
import { NumberInput, getNumberInputs } from '../domains/valueObjects/NumberInput';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';
import { TableProperty } from '@/coreDomainsModule/table';

/**
 * Store
 */
export const useAddVariableTableContentStore = defineStore('addVariableTableContent', () => {
  /**
   * uiState
   */
  const state = reactive<IAddVariableTableContentState>({
    tableType: 'default',
    isEdit: false,
    tableContentHeaders: [],
    formulaTitle: '',
    inputFormula: [],
    isValid: false,
  });

  const commonTableContentVm = new TableCommonViewModel();
  const contentHeaderName = businessContentName;

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.tableType = 'default';
    state.isEdit = false;
    state.tableContentHeaders = [];
    state.inputFormula = [];
    state.isValid = false;
    state.formulaTitle = '';
    state.variableContent = undefined;
  };

  /**
   * stateの初期化
   * @param tableType - テーブルの種類
   * @param isEdit - 編集かどうか
   * @param variableContent - 編集する変数の内容
   */
  const initState = (tableType: string, isEdit: boolean, variableContent?: TableProperty) => {
    const contentHeader = commonTableContentVm.displayHeaders[tableType];
    if (!contentHeader) {
      throw new Error('headerが存在しません');
    }
    state.tableType = tableType;
    contentHeader.forEach((header: string) => {
      const addTableHeader = addTableContentHeader(header, contentHeaderName[header]);
      if (addTableHeader.type === 'success') {
        state.tableContentHeaders.push(addTableHeader.value);
      }
    });

    additionalHeaders.forEach((header: TableContentHeaders) => {
      state.tableContentHeaders.push(header);
    });

    if (isEdit) {
      if (variableContent) {
        state.isEdit = isEdit;
        state.variableContent = variableContent;
        state.formulaTitle = variableContent.customFormulaTitle || '';
        state.inputFormula = simpleCopy(variableContent.customFormula) || [];
        // 既に登録済みなので、isValidはtrue
        state.isValid = true;
      }
    }
  };

  /**
   * inputFormulaに対して、数値の追加
   * 直前に追加されたものがnumber^追加される値がNumberInputの場合、pushせずに合体させる
   * @param numberInput - 数値
   */
  const addNumberInput = (numberInput: NumberInput) => {
    let addInput: string = numberInput;

    if (state.inputFormula.length > 0) {
      const lastInput = Number(state.inputFormula[state.inputFormula.length - 1]);

      if (typeof lastInput === 'number' && isFinite(lastInput) && getNumberInputs().includes(numberInput as NumberInput)) {
        const lastInput = state.inputFormula.pop();
        if (lastInput) {
          addInput = lastInput.concat(addInput);
        }
      }
    }

    const result = addInputFormula(state.inputFormula, addInput);
    state.isValid = result.type !== 'error';
    state.inputFormula.push(addInput);
  };

  /**
   * inputFormulaに対して、演算子の追加
   * @param operator - 演算子
   */
  const addOperator = (operator: Operator) => {
    const result = addInputFormula(state.inputFormula, operator);
    state.isValid = result.type !== 'error';
    state.inputFormula.push(operator);
  };

  /**
   * inputFormulaに対して、ヘッダーの追加
   * @param header - ヘッダー
   */
  const addHeader = (header: string) => {
    const result = addInputFormula(state.inputFormula, header);
    state.isValid = result.type !== 'error';
    state.inputFormula.push(header);
  };

  /**
   * inputFormulaのリセット
   */
  const allResetCalculate = () => {
    state.inputFormula = simpleCopy([]);
    state.isValid = true;
  };

  /**
   * inputFormulaの末尾を削除
   */
  const backCalculate = () => {
    const result = removeInputFormula(state.inputFormula);
    state.isValid = result.type !== 'error';
    state.inputFormula.pop();
  };

  /**
   * inputFormulaの保存
   * TODO: preference周りのリポジトリ化とそもそも持っているデータの構造を見直してからここ修正する
   */
  const saveCalculate = async (): Promise<Result<void, Error>> => {
    try {
      await loadPreferences();
      let formulaId = Math.floor(Math.random() * 1000000);
      if (state.isEdit) {
        // update
        formulaId = state.variableContent?.customFormulaId || formulaId;
        const variableTableHeader: TableProperty[] = myPreferences[state.tableType].variableTableHeader;
        if (!variableTableHeader)
          return {
            type: 'error',
            error: new Error('想定外のエラー'),
          };

        for (let i = 0; i < variableTableHeader.length; i++) {
          if (variableTableHeader[i].customFormulaId === formulaId) {
            variableTableHeader[i].customFormulaTitle = state.formulaTitle;
            variableTableHeader[i].customFormula = state.inputFormula;
          }
        }
      } else {
        // new
        myPreferences[state.tableType].variableTableHeader = [
          ...myPreferences[state.tableType].variableTableHeader,
          {
            isDisplay: true,
            contentName: state.formulaTitle,
            lastIndex: -1, // 使わない
            customFormulaTitle: state.formulaTitle,
            customFormula: state.inputFormula,
            customFormulaId: formulaId,
          },
        ];
      }

      myPreferences[state.tableType] = simpleCopy(myPreferences[state.tableType]);
      await savePreferences();
      return {
        type: 'success',
        value: undefined,
      };
    } catch (error) {
      return {
        type: 'error',
        error: new Error('保存に失敗しました'),
      };
    }
  };

  /**
   * inputFormulaの削除
   * @param formulaId - 削除する式のID
   */
  const deleteCalculate = async (formulaId?: number): Promise<Result<void, Error>> => {
    try {
      await loadPreferences();
      const variableTableHeader: TableProperty[] = myPreferences[state.tableType].variableTableHeader;
      if (!variableTableHeader)
        return {
          type: 'error',
          error: new Error('想定外のエラー'),
        };

      for (let i = 0; i < variableTableHeader.length; i++) {
        if (variableTableHeader[i].customFormulaId === formulaId) {
          variableTableHeader.splice(i, 1);
        }
      }

      myPreferences[state.tableType].variableTableHeader = simpleCopy(variableTableHeader);
      myPreferences[state.tableType] = simpleCopy(myPreferences[state.tableType]);
      await savePreferences();
      return {
        type: 'success',
        value: undefined,
      };
    } catch (error) {
      return {
        type: 'error',
        error: new Error('削除に失敗しました'),
      };
    }
  };

  /**
   * stateの更新
   * @param newState - 更新するstate
   */
  const updateState = (newState: Partial<IAddVariableTableContentState>) => {
    state.tableType = newState.tableType || state.tableType;
    state.tableContentHeaders = newState.tableContentHeaders || state.tableContentHeaders;
    state.formulaTitle = newState.formulaTitle || state.formulaTitle;
    state.inputFormula = newState.inputFormula || state.inputFormula;
    state.isValid = newState.isValid || state.isValid;
  };

  /**
   * inputFormulaの表示
   * 演算子以外のものを、HeaderNameに変換して表示する
   * @param formula - 入力された式
   */
  const displayFormula = (formula: InputFormula): string => {
    return formula
      .map((value) => {
        if (getOperators().includes(value as Operator) || getNumberInputs().includes(value as NumberInput)) {
          return value;
        } else {
          for (let i = 0; i < state.tableContentHeaders.length; i++) {
            if (state.tableContentHeaders[i].headerKey === value) {
              return state.tableContentHeaders[i].headerName;
            }
          }
          return value; // ありえないはず
        }
      })
      .join(' ');
  };

  return {
    state,
    $reset,
    initState,
    getNumberInputs,
    addNumberInput,
    addOperator,
    addHeader,
    allResetCalculate,
    backCalculate,
    saveCalculate,
    deleteCalculate,
    updateState,
    displayFormula,
  };
});

/**
 * uiState
 * @interface IAddVariableTableContentState
 * @property {string} tableType - テーブルの種類
 * @property {boolean} isEdit - 編集かどうか
 * @property {TableProperty} variableContent - 編集する変数の内容
 * @property {TableContentHeaders[]} tableContentHeaders - テーブルのヘッダー
 * @property {string} formulaTitle - 式のタイトル
 * @property {InputFormula} inputFormula - 入力された式
 * @property {boolean} isValid - 入力された式が正しいかどうか
 */
interface IAddVariableTableContentState {
  tableType: string;
  isEdit: boolean;
  variableContent?: TableProperty;
  tableContentHeaders: TableContentHeaders[];
  formulaTitle: string;
  inputFormula: InputFormula;
  isValid: boolean;
}
